import { platformsService } from '@/services/platforms.service';
import axios from 'axios';

const initialState = () => ({
  mergedPlatforms: [],
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  async getTenantPlatforms({ commit }) {
    commit('GET_TENANT_PLATFORMS');

    return Promise.all([platformsService.getPlatforms(), platformsService.getTenantPlatforms()])
      .then(([platforms, tenantPlatforms]) => {
        commit('GET_TENANT_PLATFORMS_SUCCESS', { platforms, tenantPlatforms });
      })
      .catch((error) => {
        commit('GET_TENANT_PLATFORMS_ERROR', error);
      });
  },
  setTenantPlatform({ commit, dispatch }, { tenantId, platformId, platformAccess }) {
    commit('SET_TENANT_PLATFORM');
    return platformsService.setTenantPlatform({ tenantId, platformId, platformAccess }).then(
      (platform) => {
        commit('SET_TENANT_PLATFORM_SUCCESS', platform);
        dispatch('userPlatforms/getUserPlatforms', null, { root: true });
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('SET_TENANT_PLATFORM_ERROR', error.response.data);
          throw error;
        }
      },
    );
  },
  async setTenantPlatformsBatch({ commit, dispatch }, { platformAccessList }) {
    if (!platformAccessList?.length) {
      return;
    }
    commit('SET_TENANT_PLATFORMS_BATCH_START');
    try {
      for (const platformAccess of platformAccessList) {
        const platform = await platformsService.setTenantPlatform({ platformAccess });
        commit('SET_TENANT_PLATFORM_BATCH_ITEM_SUCCESS', platform);
      }
      commit('SET_TENANT_PLATFORMS_BATCH_SUCCESS');
      dispatch('userPlatforms/getUserPlatforms', null, { root: true });
    } catch (error) {
      if (!axios.isCancel(error)) {
        commit('SET_TENANT_PLATFORMS_BATCH_ERROR', error.response?.data ?? error);
      }
      throw error;
    }
  },
};

const mutations = {
  GET_TENANT_PLATFORMS(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_TENANT_PLATFORMS_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  GET_TENANT_PLATFORMS_SUCCESS(state, { platforms, tenantPlatforms }) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    // Catalog-first (like modulesAccess): match by id, not array index.
    state.mergedPlatforms = (platforms || []).map((platform) => {
      const id = String(platform.id || '').toLowerCase();
      const tenantPlatform =
        (tenantPlatforms || []).find((tp) => String(tp.platformId || '').toLowerCase() === id) || {};
      return {
        ...platform,
        ...tenantPlatform,
        platformId: platform.id,
        hasAccess: !!tenantPlatform.hasAccess,
      };
    });
  },
  SET_TENANT_PLATFORM(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  SET_TENANT_PLATFORM_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
  },
  SET_TENANT_PLATFORM_SUCCESS(state, platform) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    const index = state.mergedPlatforms.findIndex((item) => item.platformId === platform.platformId);

    if (index !== -1) {
      const updatedPlatform = { ...state.mergedPlatforms[index], ...platform };
      state.mergedPlatforms.splice(index, 1, updatedPlatform);
    }
  },
  SET_TENANT_PLATFORMS_BATCH_START(state) {
    state.status = {
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  SET_TENANT_PLATFORM_BATCH_ITEM_SUCCESS(state, platform) {
    const index = state.mergedPlatforms.findIndex((item) => item.platformId === platform.platformId);

    if (index !== -1) {
      const updatedPlatform = { ...state.mergedPlatforms[index], ...platform };
      state.mergedPlatforms.splice(index, 1, updatedPlatform);
    }
  },
  SET_TENANT_PLATFORMS_BATCH_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
      rejected: false,
      error: null,
    };
  },
  SET_TENANT_PLATFORMS_BATCH_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error,
    };
  },
};

const getters = {
  mergedPlatforms: (state) => state.mergedPlatforms,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const tenantPlatforms = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
